/*
 * Темы платформы viva: «Глубокий индиго» (светлая) и «Графит и мята» (тёмная).
 *
 * Обе построены на createUnifiedTheme: палитра, шапки страниц, скругления и
 * переопределения компонентов; сами компоненты Backstage не трогаем. Общие черты —
 * округлые формы (пилюли у кнопок, чипов, полей и пунктов сайдбара, 20px у карточек),
 * тёмный сайдбар с активным пунктом-пилюлей и шрифт Manrope (полная кириллица).
 */
import {
  createUnifiedTheme,
  palettes,
  UnifiedThemeOptions,
} from '@backstage/theme';
import { WATERMARK, sectionPageThemes } from './sections';

const fontFamily = 'Manrope, "Segoe UI", system-ui, -apple-system, sans-serif';

const radius = { card: 20, control: 999, chip: 999 };

type Tokens = {
  bg: string;
  paper: string;
  paperAlt: string;
  text: string;
  textSubtle: string;
  border: string;
  primary: string;
  primarySoft: string;
  onPrimary: string;
  accent: string;
  link: string;
  linkHover: string;
  nav: {
    bg: string;
    text: string;
    active: string;
    onActive: string;
    hover: string;
  };
  /** Шапка: градиент светлой темы / свечение тёмной и цвет текста; знак раздела — theme/sections. */
  header: { colors: string[]; glow: string; fontColor: string };
  /** Логотип ViVA в сайдбаре (тёмном в обеих темах): название, первая V и акцентная V. */
  logo: { text: string; mark: string; accent: string };
  status: {
    ok: string;
    warning: string;
    error: string;
    running: string;
    pending: string;
    aborted: string;
  };
  shadow: string;
};

const indigo: Tokens = {
  bg: '#f4f5fb',
  paper: '#ffffff',
  paperAlt: '#f0f1f8',
  text: '#1b1f3a',
  textSubtle: '#6b7090',
  border: '#e3e6f2',
  primary: '#4f46e5',
  primarySoft: '#eceafd',
  onPrimary: '#ffffff',
  accent: '#0d9488',
  link: '#4f46e5',
  linkHover: '#0d9488',
  nav: {
    bg: '#1e1b4b',
    text: '#b9b7e0',
    active: '#4f46e5',
    onActive: '#ffffff',
    hover: '#2a2663',
  },
  header: {
    colors: ['#312e81', '#4338ca', '#0f766e'],
    glow: '#0f766e',
    fontColor: '#ffffff',
  },
  // сайдбар тёмный и в светлой теме: индиго на #1e1b4b даёт 2.5:1, поэтому акцентная V —
  // мята (продолжение бирюзы темы: accent #0d9488, конец градиента шапки #0f766e)
  logo: { text: '#ffffff', mark: '#ffffff', accent: '#5eead4' },
  status: {
    ok: '#16a34a',
    warning: '#d97706',
    error: '#dc2626',
    running: '#4f46e5',
    pending: '#ca8a04',
    aborted: '#6b7090',
  },
  shadow: '0 1px 2px rgba(27,31,58,.06), 0 12px 32px -20px rgba(27,31,58,.25)',
};

const graphite: Tokens = {
  bg: '#0f1115',
  paper: '#181b21',
  paperAlt: '#1f232b',
  text: '#e8eaf0',
  textSubtle: '#8b91a1',
  border: '#262a33',
  primary: '#5eead4',
  primarySoft: 'rgba(94,234,212,.14)',
  onPrimary: '#0f1115',
  accent: '#a3e635',
  link: '#5eead4',
  linkHover: '#a3e635',
  nav: {
    bg: '#0b0d11',
    text: '#9aa0ae',
    active: '#5eead4',
    onActive: '#0f1115',
    hover: '#161920',
  },
  header: {
    colors: ['#181b21', '#1f232b'],
    glow: '#5eead4',
    fontColor: '#e8eaf0',
  },
  logo: { text: '#e8eaf0', mark: '#e8eaf0', accent: '#5eead4' },
  status: {
    ok: '#4ade80',
    warning: '#fbbf24',
    error: '#f87171',
    running: '#5eead4',
    pending: '#fde047',
    aborted: '#8b91a1',
  },
  shadow: '0 0 0 1px #262a33',
};

/** Шапки страниц: градиент темы плюс водяной знак раздела — theme/sections. */
const pageThemes = (t: Tokens, mode: 'light' | 'dark') =>
  sectionPageThemes(mode, {
    colors: t.header.colors,
    glow: t.header.glow,
    paper: t.paper,
    fontColor: t.header.fontColor,
  });

const components = (t: Tokens): UnifiedThemeOptions['components'] => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: { fontFamily },
    },
  },
  BackstageSidebar: {
    styleOverrides: {
      drawer: { borderRight: `1px solid ${t.border}` },
    },
  },
  BackstageSidebarItem: {
    styleOverrides: {
      root: {
        borderRadius: radius.control,
        margin: '2px 10px',
        width: 'calc(100% - 20px)',
        height: 44,
      },
      label: { fontWeight: 600 },
      highlightable: { '&:hover': { background: t.nav.hover } },
      selected: {
        '&$root': {
          borderLeft: 'none',
          background: t.nav.active,
          color: t.nav.onActive,
        },
        '& $closedItemIcon': { paddingRight: 0 },
        '& $iconContainer': { marginLeft: 0 },
      },
    },
  },
  BackstageHeader: {
    styleOverrides: {
      // слои фона шапки: знак раздела слева с нахлёстом, волна/свечение, градиент;
      // заголовок сдвинут вправо, чтобы лечь на правую треть знака, а не на его центр
      header: {
        boxShadow: 'none',
        padding: `28px 32px 28px ${WATERMARK.textInset}px`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${WATERMARK.size}px auto, cover, auto`,
        backgroundPosition: `left ${WATERMARK.left}px top ${WATERMARK.top}px, center, center`,
      },
      title: { fontWeight: 700, letterSpacing: '-0.02em' },
      subtitle: { opacity: 0.8 },
    },
  },
  BackstageContent: {
    styleOverrides: { root: { background: t.bg } },
  },
  BackstageTable: {
    styleOverrides: {
      root: {
        borderRadius: radius.card,
        boxShadow: t.shadow,
        overflow: 'hidden',
        '& > :first-child': {
          borderRadius: `${radius.card}px ${radius.card}px 0 0`,
        },
        '& td, & th': { borderBottomColor: t.border },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: { borderRadius: radius.card },
      elevation1: { boxShadow: t.shadow },
      elevation2: { boxShadow: t.shadow },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: { borderRadius: radius.card, boxShadow: t.shadow },
    },
  },
  MuiCardHeader: {
    styleOverrides: { title: { fontWeight: 700, letterSpacing: '-0.01em' } },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: radius.control,
        textTransform: 'none',
        fontWeight: 700,
        padding: '8px 18px',
      },
      contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: radius.chip,
        fontWeight: 600,
        background: t.paperAlt,
        color: t.text,
      },
      outlined: { borderColor: t.border },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: radius.control, background: t.paper },
      notchedOutline: { borderColor: t.border },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: { borderRadius: radius.control, background: t.paperAlt },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: { '&:hover': { background: t.paperAlt } },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { borderBottomColor: t.border },
      head: {
        fontWeight: 700,
        color: t.textSubtle,
        fontSize: 12,
        letterSpacing: '0.06em',
      },
    },
  },
  MuiTooltip: {
    styleOverrides: { tooltip: { borderRadius: 12, fontSize: 12 } },
  },
  MuiAvatar: {
    styleOverrides: {
      colorDefault: { background: t.accent, color: t.onPrimary },
    },
  },
  MuiTabs: {
    styleOverrides: { indicator: { height: 3, borderRadius: 3 } },
  },
});

const build = (
  base: typeof palettes.light | typeof palettes.dark,
  t: Tokens,
  mode: 'light' | 'dark',
) =>
  createUnifiedTheme({
    fontFamily,
    defaultPageTheme: 'home',
    pageTheme: pageThemes(t, mode),
    palette: {
      ...base,
      primary: { main: t.primary, contrastText: t.onPrimary },
      secondary: { main: t.accent },
      background: { default: t.bg, paper: t.paper },
      text: { primary: t.text, secondary: t.textSubtle },
      divider: t.border,
      border: t.border,
      textContrast: t.text,
      textSubtle: t.textSubtle,
      textVerySubtle: t.border,
      link: t.link,
      linkHover: t.linkHover,
      status: t.status,
      navigation: {
        ...base.navigation,
        background: t.nav.bg,
        indicator: t.nav.active,
        color: t.nav.text,
        selectedColor: t.nav.onActive,
        navItem: { hoverBackground: t.nav.hover },
        submenu: { background: t.nav.hover },
      },
      tabbar: { indicator: t.primary },
      pinSidebarButton: { icon: t.nav.text, background: t.nav.hover },
      infoBackground: t.primarySoft,
      infoText: t.primary,
    },
    components: components(t),
  });

/** A · Глубокий индиго — светлая тема по умолчанию. */
export const vivaLightTheme = build(palettes.light, indigo, 'light');

/** C · Графит и мята — тёмная тема. */
export const vivaDarkTheme = build(palettes.dark, graphite, 'dark');

/** Токены, которые нужны вне MUI (логотип, иконки в сайдбаре). */
export const vivaTokens = { light: indigo, dark: graphite };

/** Токены по типу активной темы — для компонентов на makeStyles(theme => …). */
export const vivaTokensFor = (type: 'light' | 'dark') =>
  type === 'dark' ? graphite : indigo;
